import { useGetVoyageSearchs } from 'api-hooks/voyage/query';
import { CruiseNotFound } from 'assets/images/svg';
import NavigationEnum from 'common/navigation';
import { AspectRatio } from 'components/commons/aspect-ratio';
import Stack from 'components/commons/stack';
import Text from 'components/commons/text';
import Button from 'components/elements/button';
import Checkbox from 'components/elements/checkbox/checkbox';
import { Skeleton } from 'components/widgets/skeleton';
import useMappingTicketItems from 'hooks/use-mapping-ticket-items';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import qs from 'qs';
import * as React from 'react';

import SearchTicketLoading from './search-ticket-loading';
import SelectShippingRoute from './select-shipping-route';
import ShippingRoute from './select-shipping-route-dialog';
import { Section, SectionContent } from './style';
import TicketCard from './ticket-card';

export default function SearchTicket() {
  const { t } = useTranslation();
  const { query, push, pathname } = useRouter();
  const [facility, setFacility] = React.useState<number>(0);
  const [open, setOpen] = React.useState<boolean>(false);
  const openHandler = (value: boolean) => {
    setOpen(value);
  };
  const { data, isLoading, isError, error } = useGetVoyageSearchs({
    params: {
      ...query,
      type: query.type === '2' ? '0' : query.type,
      facility,
    },
  });

  React.useEffect(() => {
    try {
      sessionStorage?.setItem('prevUrl', `${pathname}?${qs.stringify(query)}`);
    } catch (e) {
    } finally {
    }
  }, [query, pathname]);

  const isMainEmpty = data?.data?.mainVoyages?.length === 0;
  const isAltEmpty = data?.data?.altVoyages?.length === 0;

  const mainVoyages = useMappingTicketItems({
    type: 'default',
    facility,
    data: data?.data?.mainVoyages || [],
  });

  const altVoyages = useMappingTicketItems({
    type: 'default',
    facility,
    data: data?.data?.altVoyages || [],
  });

  return (
    <>
      {isLoading ? (
        <SearchTicketLoading />
      ) : (
        <>
          {data?.data && (
            <SelectShippingRoute
              from={data.data.searchParams.fromHarbor}
              departureTime={data.data.searchParams.date}
              passenger={data.data.searchParams.passengersCount}
              to={data.data.searchParams.toHarbor}
              vehicleType={data.data.searchParams.vehiclesCount}
              openParent={openHandler}
              parentOpen={open}
            />
          )}
        </>
      )}
      {(open || isError) && (
        <ShippingRoute
          onClose={() => {
            setOpen(false);
          }}
          onSubmit={(values) => {
            push(`${NavigationEnum.searchShipping}?${values}`);
            setOpen(false);
          }}
          type={query.type?.toString() || ''}
          cancelable={!isError}
        />
      )}
      {!open && (
        <Section>
          <SectionContent>
            {isLoading ? (
              <AspectRatio ratio={16 / 4}>
                <Skeleton css={{ height: '100%', borderRadius: 8 }} />
              </AspectRatio>
            ) : (
              <>
                {!isError ? (
                  <>
                    {isMainEmpty ? (
                      //when alternative empty, main is empty too
                      <>
                        <Stack
                          justify="center"
                          alignItems="center"
                          rootCss={{ minHeight: 300 }}
                          gap="sm"
                        >
                          <Stack.Item>
                            <CruiseNotFound />
                          </Stack.Item>
                          <Stack.Item>
                            <Text
                              variant="headline5"
                              weight="bold"
                              align="center"
                            >
                              {t('common:cruise_not_found')}
                            </Text>
                          </Stack.Item>
                          <Stack.Item>
                            <Text variant="body4" align="center">
                              {t('common:sorry_cruise_not_available')}
                            </Text>
                          </Stack.Item>
                          <Stack.Item>
                            <Text variant="body4" align="center">
                              {t('common:please_find_different_schedule')}
                            </Text>
                          </Stack.Item>
                        </Stack>
                      </>
                    ) : (
                      mainVoyages.map((item, idx) => (
                        <TicketCard
                          {...item}
                          key={item.voyageCode}
                          css={{ mt: idx !== 0 ? 16 : 0 }}
                        />
                      ))
                    )}
                    {!isAltEmpty && (
                      <>
                        <Text variant="headline5" css={{ mt: 36 }}>
                          {t('search:alternative_route')}
                        </Text>
                        <Text variant="body4" css={{ mt: 16, mb: 24 }}>
                          {t('search:cruise_not_found_alternative')}
                        </Text>
                        {query.type !== '1' && (
                          <Checkbox
                            name=""
                            label={t('common:with_facilities')}
                            labelCss={{ mb: 16 }}
                            isSelected={!!facility}
                            onChange={(isSelected) =>
                              setFacility(isSelected ? 1 : 0)
                            }
                          />
                        )}
                        {altVoyages.map((item, idx) => {
                          return (
                            <TicketCard
                              key={item.voyageCode}
                              css={{ mt: idx !== 0 ? 16 : 0 }}
                              {...item}
                            />
                          );
                        })}
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <Stack
                      justify="center"
                      alignItems="center"
                      rootCss={{ minHeight: 300 }}
                      gap="sm"
                    >
                      <Stack.Item>
                        <CruiseNotFound />
                      </Stack.Item>
                      <Stack.Item>{error.message}</Stack.Item>
                      <Stack.Item>
                        <Button
                          onClick={() => {
                            push(NavigationEnum.home);
                          }}
                        >
                          {t('common:back_to_home')}
                        </Button>
                      </Stack.Item>
                    </Stack>
                  </>
                )}
              </>
            )}
          </SectionContent>
        </Section>
      )}
    </>
  );
}
